// Phase 9 — operator command palette.
//
// Triggered by:
//   • Cmd+K (Mac) / Ctrl+K (Win / Linux) — global hotkey.
//   • The search trigger button in the AppShell header.
//
// Search categories:
//   a) Pages           — every authenticated route (from routeMeta).
//   b) Recent runs     — last 20 runs the user has opened (localStorage).
//   c) Models          — Phase 1 registry rows (lazy fetch from /api/models).
//   d) Imported logs   — Phase 7 (lazy fetch from /api/logs).
//   e) Presets         — Phase 0/8 (lazy fetch from /api/presets).
//
// With empty input we surface the 8 most recent palette selections to
// give the operator the "jump back" behaviour Cmd+K users expect.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Command } from "cmdk";
import { useNavigate } from "react-router-dom";
import {
    BookOpen,
    Clock,
    Cpu,
    Database,
    FileText,
    Layers,
    LayoutGrid,
    PlayCircle,
    Search,
    ShieldCheck,
    SlidersHorizontal,
} from "lucide-react";
import { ROUTES, groupById, fillPath } from "@/lib/routeMeta";
import useUserRole from "@/hooks/useUserRole";
import {
    logs as logsApi,
    models as modelsApi,
    presets as presetsApi,
    sim as simApi,
} from "@/api/endpoints";

const RECENT_KEY = "uavsimnet_recent_runs_v1";
const SELECTIONS_KEY = "uavsimnet_palette_selections_v1";
// Phase 18 Ask 4.1 — per-collection recents helpers live in
// ./cmdPaletteRecents so they can be unit-tested without dragging
// in react-router (which jest-resolves poorly under our craco config).
import {
    readCollectionRecents,
    pushCollectionRecent,
    buildPaletteEmptyQueryGroups,
} from "./cmdPaletteRecents";
export { readCollectionRecents, pushCollectionRecent };

// -----------------------------------------------------------------------
// localStorage helpers (per-user keys — stamped with the username so two
// operators sharing a machine don't blend recents).
// -----------------------------------------------------------------------
function storageKey(base, username) {
    return username ? `${base}::${username}` : base;
}

export function readRecentRuns(username) {
    try {
        const raw = localStorage.getItem(storageKey(RECENT_KEY, username));
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, 20) : [];
    } catch {
        return [];
    }
}

export function pushRecentRun(username, run) {
    if (!run || !run.id) return;
    try {
        const key = storageKey(RECENT_KEY, username);
        const raw = localStorage.getItem(key);
        const arr = raw ? JSON.parse(raw) : [];
        const filtered = Array.isArray(arr) ? arr.filter((r) => r.id !== run.id) : [];
        const next = [{ id: run.id, name: run.name || "", status: run.status || "", ts: Date.now() }, ...filtered].slice(0, 20);
        localStorage.setItem(key, JSON.stringify(next));
    } catch {
        /* noop */
    }
}

function readSelections(username) {
    try {
        const raw = localStorage.getItem(storageKey(SELECTIONS_KEY, username));
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, 8) : [];
    } catch {
        return [];
    }
}

function pushSelection(username, item) {
    if (!item) return;
    try {
        const key = storageKey(SELECTIONS_KEY, username);
        const raw = localStorage.getItem(key);
        const arr = raw ? JSON.parse(raw) : [];
        const filtered = Array.isArray(arr) ? arr.filter((i) => i.key !== item.key) : [];
        localStorage.setItem(key, JSON.stringify([item, ...filtered].slice(0, 8)));
    } catch {
        /* noop */
    }
}

// Phase 18 Ask 4.1 — per-collection recents helpers are re-exported
// from ./cmdPaletteRecents above. The legacy inline implementation
// below has been removed in favour of the shared module.

// -----------------------------------------------------------------------
// Palette component — controlled. Parent (AppShell) owns `open`.
// -----------------------------------------------------------------------
export default function CommandPalette({ open, onOpenChange, username }) {
    const navigate = useNavigate();
    const { isAdmin } = useUserRole();
    const [q, setQ] = useState("");

    // Lazy-loaded category rows.
    const [models, setModels] = useState([]);
    const [logs, setLogs] = useState([]);
    const [presets, setPresets] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [recent, setRecent] = useState(() => readRecentRuns(username));
    const [selections, setSelections] = useState(() => readSelections(username));
    const [fetched, setFetched] = useState(false);
    const fetchedRef = useRef(false);

    // Global hotkey (Cmd+K / Ctrl+K).
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                onOpenChange(!open);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onOpenChange]);

    // Reset query when opening; refresh recents + lazy-fetch lists.
    useEffect(() => {
        if (!open) return;
        setQ("");
        setRecent(readRecentRuns(username));
        setSelections(readSelections(username));
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        (async () => {
            try {
                const [m, l, p, sc] = await Promise.all([
                    modelsApi.list().catch(() => []),
                    logsApi.list().catch(() => []),
                    presetsApi.list().catch(() => []),
                    simApi.scenarios.list().catch(() => []),
                ]);
                setModels(Array.isArray(m) ? m : m?.items || []);
                setLogs(Array.isArray(l) ? l : l?.items || []);
                setPresets(Array.isArray(p) ? p : p?.items || []);
                // Newest first — the Workbench lists "recent 10".
                const scArr = Array.isArray(sc) ? sc : sc?.items || [];
                setScenarios(
                    [...scArr]
                        .sort((a, b) =>
                            (b.updated_at || "").localeCompare(a.updated_at || ""),
                        )
                        .slice(0, 10),
                );
            } finally {
                setFetched(true);
            }
        })();
    }, [open, username]);

    const go = useCallback(
        (to, item) => {
            pushSelection(username, item);
            // Phase 18 Ask 4.1 — also stamp the item into its per-
            // collection bucket so the palette can surface "Recent
            // <collection>" on next open even after intervening picks
            // from other collections.
            if (item && item.collection) {
                pushCollectionRecent(username, item.collection, item);
            }
            onOpenChange(false);
            navigate(to);
        },
        [navigate, onOpenChange, username],
    );

    // Flatten ROUTES into palette rows, filtered by role.
    const pageRows = useMemo(
        () =>
            ROUTES.filter(
                (r) => !r.hiddenFromPalette && (r.group !== "admin" || isAdmin) && !r.path.includes(":"),
            ).map((r) => ({
                key: `page:${r.id}`,
                to: fillPath(r.path, {}),
                label: r.label,
                description: r.description,
                groupLabel: groupById(r.group)?.label || "",
                kind: "page",
                keywords: [r.label, r.description, ...(r.keywords || []), groupById(r.group)?.label || ""].join(" "),
            })),
        [isAdmin],
    );

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4 bg-black/60 backdrop-blur-sm"
            onClick={(e) => {
                if (e.target === e.currentTarget) onOpenChange(false);
            }}
            data-testid="cmdk-backdrop"
        >
            <Command
                label="Command palette"
                className="w-full max-w-xl rounded-lg border border-ux-border-1 bg-ux-bg-1 shadow-2xl overflow-hidden"
                data-testid="cmdk-root"
                onKeyDown={(e) => {
                    if (e.key === "Escape") onOpenChange(false);
                }}
            >
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-ux-border-1">
                    <Search className="w-4 h-4 text-ux-text-3 shrink-0" />
                    <Command.Input
                        autoFocus
                        value={q}
                        onValueChange={setQ}
                        placeholder="Jump to a page, run, model, log, preset…"
                        className="flex-1 bg-transparent outline-none text-[14px] text-ux-text-1 placeholder:text-ux-text-3"
                        data-testid="cmdk-input"
                    />
                    <kbd className="text-[10px] font-mono text-ux-text-3 border border-ux-border-1 rounded px-1.5 py-0.5">
                        ESC
                    </kbd>
                </div>
                <Command.List
                    className="max-h-[50vh] overflow-y-auto py-2"
                    data-testid="cmdk-list"
                >
                    <Command.Empty className="px-3 py-6 text-center text-[13px] text-ux-text-3">
                        No results.
                    </Command.Empty>

                    {/* Empty-input quick jumps. */}
                    {!q && selections.length > 0 && (
                        <Command.Group heading="Recent">
                            {selections.map((s) => (
                                <PaletteItem
                                    key={`sel:${s.key}`}
                                    testId={`cmdk-row-recent-${s.key}`}
                                    icon={<Clock className="w-4 h-4 text-ux-text-3" />}
                                    label={s.label}
                                    hint={s.hint || ""}
                                    onSelect={() => go(s.to, s)}
                                />
                            ))}
                        </Command.Group>
                    )}

                    {/* Phase 18 close-off Ask B — per-collection MRU
                        groups in the empty-query view. Each non-empty
                        bucket renders its own Command.Group. */}
                    {!q && buildPaletteEmptyQueryGroups(username).map((group) => (
                        <Command.Group
                            key={`recents:${group.collection}`}
                            heading={group.label}
                        >
                            {group.items.map((it) => (
                                <PaletteItem
                                    key={`recents:${group.collection}:${it.key}`}
                                    testId={`cmdk-row-recents-${group.collection}-${it.key}`}
                                    icon={<Clock className="w-4 h-4 text-ux-text-3" />}
                                    label={it.label}
                                    hint={it.hint || group.collection}
                                    onSelect={() => go(it.to, it)}
                                />
                            ))}
                        </Command.Group>
                    ))}

                    <Command.Group heading="Pages">
                        {pageRows.map((p) => (
                            <PaletteItem
                                key={p.key}
                                testId={`cmdk-row-${p.key.replace(/[^a-zA-Z0-9-]/g, "-")}`}
                                icon={iconForGroup(p.groupLabel)}
                                label={p.label}
                                hint={`${p.groupLabel} · ${p.description}`}
                                value={p.keywords}
                                onSelect={() =>
                                    go(p.to, {
                                        key: p.key,
                                        to: p.to,
                                        label: p.label,
                                        hint: p.groupLabel,
                                        collection: "pages",
                                    })
                                }
                            />
                        ))}
                    </Command.Group>

                    {recent.length > 0 && (
                        <Command.Group heading="Recent runs">
                            {recent.map((r) => {
                                const to = `/analyse/runs/${r.id}`;
                                const label = r.name || r.id;
                                return (
                                    <PaletteItem
                                        key={`run:${r.id}`}
                                        testId={`cmdk-row-run-${r.id}`}
                                        icon={<PlayCircle className="w-4 h-4 text-ux-text-3" />}
                                        label={label}
                                        hint={`${r.id.slice(0, 8)}… · ${r.status || "—"}`}
                                        onSelect={() =>
                                            go(to, {
                                                key: `run:${r.id}`,
                                                to,
                                                label,
                                                hint: `${r.id.slice(0, 8)}…`,
                                                collection: "runs",
                                            })
                                        }
                                    />
                                );
                            })}
                        </Command.Group>
                    )}

                    {scenarios.length > 0 && (
                        <Command.Group heading="Scenarios">
                            {scenarios.map((s) => {
                                const sid = s.id || s._id;
                                // Default deep-link section is the first section the
                                // operator typically cares about. Hash syntax matches
                                // WorkbenchPage's parseSectionFromHash().
                                const to = `/workbench/${sid}#section=airframe`;
                                const label = s.name || sid;
                                return (
                                    <PaletteItem
                                        key={`scenario:${sid}`}
                                        testId={`cmdk-row-scenario-${sid}`}
                                        icon={<SlidersHorizontal className="w-4 h-4 text-ux-text-3" />}
                                        label={label}
                                        hint={`${s.status || "draft"} · ${s.owner_username || ""}`}
                                        onSelect={() =>
                                            go(to, {
                                                key: `scenario:${sid}`,
                                                to,
                                                label,
                                                hint: "Scenarios",
                                                collection: "scenarios",
                                            })
                                        }
                                    />
                                );
                            })}
                        </Command.Group>
                    )}

                    {models.length > 0 && (
                        <Command.Group heading="Models">
                            {models.slice(0, 40).map((m) => {
                                const mid = m.id || m.model_id || m._id;
                                const to = `/models/registry`;
                                return (
                                    <PaletteItem
                                        key={`model:${mid}`}
                                        testId={`cmdk-row-model-${mid}`}
                                        icon={<Cpu className="w-4 h-4 text-ux-text-3" />}
                                        label={m.name || mid}
                                        hint={`${m.kind || "model"} · v${m.version || "?"} · ${m.label || ""}`}
                                        onSelect={() =>
                                            go(to, {
                                                key: `model:${mid}`,
                                                to,
                                                label: m.name || mid,
                                                hint: "model",
                                                collection: "models",
                                            })
                                        }
                                    />
                                );
                            })}
                        </Command.Group>
                    )}

                    {logs.length > 0 && (
                        <Command.Group heading="Imported logs">
                            {logs.slice(0, 40).map((l) => {
                                const lid = l.id || l.log_id || l._id;
                                const to = `/models/logs`;
                                return (
                                    <PaletteItem
                                        key={`log:${lid}`}
                                        testId={`cmdk-row-log-${lid}`}
                                        icon={<FileText className="w-4 h-4 text-ux-text-3" />}
                                        label={l.name || lid || "log"}
                                        hint={`${l.format || "log"} · ${l.sha256 ? l.sha256.slice(0, 8) : ""}`}
                                        onSelect={() =>
                                            go(to, {
                                                key: `log:${lid}`,
                                                to,
                                                label: l.name || lid,
                                                hint: l.format,
                                                collection: "logs",
                                            })
                                        }
                                    />
                                );
                            })}
                        </Command.Group>
                    )}

                    {presets.length > 0 && (
                        <Command.Group heading="Presets">
                            {presets.slice(0, 40).map((p) => {
                                const pid = p.id || `${p.family}/${p.name}`;
                                const to = p.family && p.name ? `/plan/scenarios/${p.family}/${p.name}` : `/plan/scenarios`;
                                return (
                                    <PaletteItem
                                        key={`preset:${pid}`}
                                        testId={`cmdk-row-preset-${pid.replace(/[^a-zA-Z0-9-]/g, "-")}`}
                                        icon={<Layers className="w-4 h-4 text-ux-text-3" />}
                                        label={p.name || pid}
                                        hint={`${p.family || ""} ${p.summary || ""}`.trim()}
                                        onSelect={() =>
                                            go(to, {
                                                key: `preset:${pid}`,
                                                to,
                                                label: p.name || pid,
                                                hint: p.family || "preset",
                                                collection: "presets",
                                            })
                                        }
                                    />
                                );
                            })}
                        </Command.Group>
                    )}

                    {!fetched && (
                        <div className="px-3 py-3 text-[12px] text-ux-text-3 font-mono">
                            loading models / logs / presets…
                        </div>
                    )}
                </Command.List>
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-ux-border-1 text-[11px] text-ux-text-3 font-mono">
                    <span>
                        <kbd className="border border-ux-border-1 rounded px-1">↑</kbd>{" "}
                        <kbd className="border border-ux-border-1 rounded px-1">↓</kbd>{" "}
                        to move ·{" "}
                        <kbd className="border border-ux-border-1 rounded px-1">Enter</kbd>{" "}
                        to go
                    </span>
                    <span>
                        <kbd className="border border-ux-border-1 rounded px-1">⌘/Ctrl</kbd>
                        +<kbd className="border border-ux-border-1 rounded px-1">K</kbd> anywhere
                    </span>
                </div>
            </Command>
        </div>
    );
}

function PaletteItem({ testId, icon, label, hint, onSelect, value }) {
    return (
        <Command.Item
            value={value || `${label} ${hint}`}
            onSelect={onSelect}
            className="group flex items-center gap-2.5 px-3 py-2 mx-2 rounded-md cursor-pointer text-[13px] data-[selected=true]:bg-ux-bg-2 text-ux-text-2 hover:text-ux-text-1"
            data-testid={testId}
        >
            <span className="shrink-0">{icon}</span>
            <span className="flex-1 min-w-0 flex flex-col">
                <span className="text-ux-text-1 truncate">{label}</span>
                {hint && (
                    <span className="text-[11px] text-ux-text-3 truncate">{hint}</span>
                )}
            </span>
        </Command.Item>
    );
}

function iconForGroup(groupLabel) {
    switch (groupLabel) {
        case "PLAN":
            return <SlidersHorizontal className="w-4 h-4 text-ux-text-3" />;
        case "RUN":
            return <PlayCircle className="w-4 h-4 text-ux-text-3" />;
        case "ANALYSE":
            return <LayoutGrid className="w-4 h-4 text-ux-text-3" />;
        case "MODELS & DATA":
            return <Database className="w-4 h-4 text-ux-text-3" />;
        case "REVIEW":
            return <BookOpen className="w-4 h-4 text-ux-text-3" />;
        case "ADMIN":
            return <ShieldCheck className="w-4 h-4 text-ux-text-3" />;
        default:
            return <Search className="w-4 h-4 text-ux-text-3" />;
    }
}
